import React from "react";
import {
  TileWraperVariants,
  TileWrapperDynamicZoneModel,
} from "@models/strapi-components/tile-wrapper";
import Tile from "./Tile";
import { twMerge } from "tailwind-merge";
import ImageRenderer from "@components/common/Image";
import Link from "@components/common/Link";
import { Variants, motion } from "framer-motion";
import Title from "@components/common/Title";

export interface TileWrapperPropsModel {
  componentData: TileWrapperDynamicZoneModel;
  offsetClass?: string;
}

const wrapperVariant: Variants = {
  onscreen: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const TileWrapper: React.FC<TileWrapperPropsModel> = ({
  componentData,
  offsetClass,
}) => {
  const {
    title,
    subTitle,
    tiles,
    learnMoreLink,
    variant = TileWraperVariants.BASIC,
    tileWrapperImage,
    titleClassName = "",
    learnMoreText,
    bgColor,
  } = componentData;

  const tileLength = tiles?.length ?? 0;

  const tileArrayClassName = [
    "sm:flex",
    "sm:flex",
    "sm:flex xl:justify-center gap-6",
    "flex flex-col sm:flex-row gap-6 mt-8",
    "grid grid-cols-1 sm:grid sm:grid-cols-2 sm:grid-row-2 gap-6 mt-8",
    "flex-wrap justify-center gap-8 xl:max-w-[1000px] mx-auto mt-8",
    "",
    "",
    "",
    "",
    "mt-8 grid grid-cols-1 sm:grid gap-6 sm:grid-cols-2",
  ];

  const variantClasses: Record<TileWraperVariants, Record<string, string>> = {
    [TileWraperVariants.BASIC]: {
      wrapperClassName: "py-10 xl:py-20 px-8 sm:flex sm:justify-center",
      tileAndTextWrapperClassName:
        "xl:text-center xl:flex xl:flex-col xl:items-center",
      subTitleClassName: "text-center  sm:max-w-[50%] mx-auto xl:max-w-[428px]",
      tilesWrapperClassName:
        "flex flex-col items-center sm:items-stretch sm:grid sm:grid-cols-2 gap-8 mt-6 xl:mt-0 xl:mt-[54px]",
      tileClassName: "max-w-[400px] px-12 py-[50px] rounded-xl text-left",
      variantTitleClassName: "text-center lg:max-xl-[60%] xl:max-w-[80%]",
    },
    [TileWraperVariants.IMAGE_RIGHT]: {
      wrapperClassName:
        "py-12 md:py-[60px] xl:py-20 px-8 xl:px-40 flex items-center justify-between gap-12 flex-col xl:flex-row",
      tileAndTextWrapperClassName: "max-w-[740px] text-center xl:text-left",
      subTitleClassName: "mx-auto lg:max-w-[60%] xl:max-w-[80%] xl:ml-0 mb-2",
      tilesWrapperClassName:
        "flex flex-col items-center sm:flex-row sm:gap-6 sm:items-stretch",
      tileClassName:
        "max-w-[358px] px-6 py-8 rounded-xl mt-5 text-left basis-1/2",
      variantTitleClassName: "lg:max-xl-[60%] xl:max-w-[80%]",
    },

    [TileWraperVariants.IMAGE_LEFT]: {
      wrapperClassName:
        "py-12 md:py-[60px] xl:py-20 px-8 xl:px-40 flex items-center justify-between gap-12 flex-col xl:flex-row-reverse",
      tileAndTextWrapperClassName: "max-w-[740px] text-center xl:text-left",
      subTitleClassName: "mx-auto lg:max-w-[60%] xl:max-w-[80%] xl:ml-0 mb-2",
      tilesWrapperClassName:
        "flex flex-col items-center sm:flex-row sm:gap-6 sm:items-stretch",
      tileClassName:
        "max-w-[358px] px-6 py-8 rounded-xl mt-5 text-left basis-1/2",
      variantTitleClassName: "lg:max-xl-[60%] xl:max-w-[80%]",
    },
    [TileWraperVariants.DYNAMIC]: {
      wrapperClassName: "py-20 px-8",
      tileAndTextWrapperClassName: "flex flex-col items-center",
      subTitleClassName: "text-center md:max-w-[50%] xl:max-w-[40%] mx-auto",
      tilesWrapperClassName: twMerge("flex", tileArrayClassName[tileLength]),
      tileClassName: "max-w-[300px] px-6 py-8 rounded-xl text-left basis-full",
      variantTitleClassName:
        "text-center md:max-w-[60%] xl:max-w-[50%] mx-auto",
    },
    [TileWraperVariants.PIM]: {
      wrapperClassName: "py-20 px-8 xl:px-[140px]",
      tileAndTextWrapperClassName: "flex flex-col items-center",
      subTitleClassName:
        "text-center sm:text-left md:max-w-[60%] mx-auto sm:ml-0",
      tilesWrapperClassName: twMerge(
        "flex flex-col sm:flex-row",
        tileArrayClassName[tileLength],
        "justify-start items-center sm:items-stretch w-full"
      ),
      tileClassName:
        "max-w-[300px] px-6 py-8 rounded-xl mt-0 first:mt-8 sm:mt-8 text-left basis-full",
      variantTitleClassName:
        "text-center sm:text-left md:max-w-[60%] xl:max-w-[70%] mx-auto sm:ml-0",
    },
  };

  const {
    wrapperClassName,
    tileAndTextWrapperClassName,
    tileClassName,
    subTitleClassName,
    tilesWrapperClassName,
    variantTitleClassName,
  } = variantClasses[variant] ?? {};

  const renderTiles = () => {
    return tiles.map((oneTile) => {
      return (
        <Tile
          {...oneTile}
          tileClassName={tileClassName}
          key={oneTile.id}
          color={oneTile?.color ?? "#FFFFFF"}
        />
      );
    });
  };

  return (
    <motion.div
      className={twMerge(wrapperClassName, "xl:py-20 xl:mt-auto", offsetClass)}
      initial="offscreen"
      whileInView="onscreen"
      viewport={{ once: true, amount: 0.08 }}
      variants={wrapperVariant}
      style={{ backgroundColor: bgColor ?? "#FFFFFF" }}
    >
      <div className={tileAndTextWrapperClassName}>
        <Title
          variant="h2"
          titleText={title}
          className={twMerge(titleClassName, variantTitleClassName)}
        />
        <Title
          variant="paragraph"
          titleText={subTitle}
          className={subTitleClassName}
        />
        {learnMoreLink && learnMoreText ? (
          <div>
            <Link href={learnMoreLink} linkText={learnMoreText} />
          </div>
        ) : (
          <></>
        )}
        <div className={tilesWrapperClassName}>{renderTiles()}</div>
      </div>
      {tileWrapperImage && (
        <div className="w-full xl:w-1/2 grow-0 shrink-0 items-center justify-center hidden lg:flex xl:justify-start">
          <ImageRenderer image={tileWrapperImage} />{" "}
        </div>
      )}
    </motion.div>
  );
};
export default TileWrapper;
